/**
 * Mental Poker Implementation for Decentralized Card Games
 * 
 * This module implements the cryptographic protocols for Mental Poker,
 * allowing multiple players to play card games without a trusted dealer.
 * 
 * Key Features:
 * - Commutative encryption (order-independent encryption/decryption)
 * - Multi-party card shuffling with cryptographic proofs
 * - Secure card dealing without revealing information to other players
 * - Zero-knowledge proofs for shuffle verification
 */

import { CryptoUtils } from './crypto.js';
import type { 
  MentalPokerContext, 
  ShuffleProof, 
  CardEncryption, 
  EncryptionLayer,
  DecryptionKey 
} from '../types/crypto.js';

/**
 * Standard 52-card deck in string format
 */
export const STANDARD_DECK = [
  // Clubs
  'AC', '2C', '3C', '4C', '5C', '6C', '7C', '8C', '9C', 'TC', 'JC', 'QC', 'KC',
  // Diamonds  
  'AD', '2D', '3D', '4D', '5D', '6D', '7D', '8D', '9D', 'TD', 'JD', 'QD', 'KD',
  // Hearts
  'AH', '2H', '3H', '4H', '5H', '6H', '7H', '8H', '9H', 'TH', 'JH', 'QH', 'KH',
  // Spades
  'AS', '2S', '3S', '4S', '5S', '6S', '7S', '8S', '9S', 'TS', 'JS', 'QS', 'KS'
] as const;

/**
 * Mental Poker Game Engine
 * Handles all cryptographic operations for secure card games
 */
export class MentalPoker {
  private gameId: string;
  private playerIds: string[];
  private playerKeys: Map<string, CryptoKeyPair> = new Map();
  private encryptedDeck: CardEncryption[] = [];
  private shuffleProofs: ShuffleProof[] = [];
  private decryptionKeys: Map<string, Map<number, DecryptionKey>> = new Map();

  constructor(gameId: string, playerIds: string[]) {
    this.gameId = gameId;
    this.playerIds = [...playerIds];
  }

  /**
   * Initialize Mental Poker context for all players
   * Each player must generate their own encryption keys
   */
  async initializeGame(): Promise<MentalPokerContext> {
    // Generate key pairs for each player
    for (const playerId of this.playerIds) {
      const keyPair = await CryptoUtils.generateKeyPair();
      this.playerKeys.set(playerId, keyPair);
    }

    // Generate master seed for randomness (combined from all players)
    const playerSeeds = new Map<string, Uint8Array>();
    for (const playerId of this.playerIds) {
      const seed = CryptoUtils.generateSeed(32);
      playerSeeds.set(playerId, seed);
    }

    const masterSeed = CryptoUtils.combineSeeds([...playerSeeds.values()]);

    // Initialize encrypted deck
    await this.initializeEncryptedDeck();

    return {
      gameId: this.gameId,
      players: this.playerIds,
      masterSeed,
      playerSeeds,
      shuffleProofs: this.shuffleProofs,
      encryptionKeys: await this.getEncryptionKeys()
    };
  }

  /**
   * Create encrypted deck with each card encrypted by all players
   * Uses commutative encryption so order of encryption doesn't matter
   */
  private async initializeEncryptedDeck(): Promise<void> {
    this.encryptedDeck = [];

    for (let cardIndex = 0; cardIndex < STANDARD_DECK.length; cardIndex++) {
      const card = STANDARD_DECK[cardIndex];
      const encryptionLayers: EncryptionLayer[] = [];

      // Start with plaintext card
      let encryptedData: string = card;

      // Apply each player's encryption layer (commutatively)
      for (const playerId of this.playerIds) {
        const keyPair = this.playerKeys.get(playerId);
        if (!keyPair) throw new Error(`No key pair for player ${playerId}`);

        // Generate encryption key for this card and player
        const cardKey = await this.generateCardEncryptionKey(playerId, cardIndex);
        
        // Encrypt with this player's key
        encryptedData = await CryptoUtils.encrypt(encryptedData, cardKey);
        
        // Export public key for verification
        const publicKeyBuffer = await crypto.subtle.exportKey('spki', keyPair.publicKey);
        const publicKeyB64 = CryptoUtils.arrayBufferToBase64(publicKeyBuffer);
        const encryptionKeyB64 = CryptoUtils.arrayBufferToBase64(cardKey);

        encryptionLayers.push({
          playerId,
          publicKey: publicKeyB64,
          encryptionKey: encryptionKeyB64
        });
      }

      this.encryptedDeck.push({
        cardIndex,
        encryptedData,
        encryptionLayers
      });
    }
  }

  /**
   * Generate deterministic encryption key for a specific card and player
   * This ensures the same key is always generated for the same card/player combo
   */
  private async generateCardEncryptionKey(playerId: string, cardIndex: number): Promise<Uint8Array> {
    // Use deterministic seed generation based on game and player
    const playerSpecificSeed = await CryptoUtils.hash(this.gameId + playerId + cardIndex);
    
    // Combine player ID and card index to create unique key
    const combinedData = playerSpecificSeed + cardIndex.toString().padStart(8, '0');
    return CryptoUtils.hexToArrayBuffer(await CryptoUtils.hash(combinedData));
  }

  /**
   * Shuffle the encrypted deck using cryptographically secure randomness
   * Each player should perform their own shuffle to ensure fairness
   */
  async shuffleDeck(playerId: string): Promise<ShuffleProof> {
    if (!this.playerIds.includes(playerId)) {
      throw new Error(`Player ${playerId} not in game`);
    }

    // Generate cryptographic proof of shuffle
    const shuffleSeed = CryptoUtils.generateSeed(32);
    const commitment = await CryptoUtils.createCommitment(shuffleSeed, shuffleSeed);
    
    // Create shuffle permutation based on seed
    const permutation = await this.generateShufflePermutation(shuffleSeed);
    
    // Apply permutation to encrypted deck
    const originalOrder = [...this.encryptedDeck];
    this.encryptedDeck = permutation.map(index => originalOrder[index]);

    // Generate cryptographic proof
    const keyPair = this.playerKeys.get(playerId);
    if (!keyPair) throw new Error(`No key pair for player ${playerId}`);

    const proofData = {
      playerId,
      originalOrder: originalOrder.map(card => card.cardIndex),
      newOrder: this.encryptedDeck.map(card => card.cardIndex),
      shuffleSeed: CryptoUtils.arrayBufferToBase64(shuffleSeed)
    };

    const signature = await CryptoUtils.sign(proofData, keyPair.privateKey);

    const shuffleProof: ShuffleProof = {
      playerId,
      commitment,
      nonce: shuffleSeed,
      signature,
      zkProof: await this.generateZKShuffleProof(shuffleSeed, permutation)
    };

    this.shuffleProofs.push(shuffleProof);
    return shuffleProof;
  }

  /**
   * Generate cryptographically secure permutation for shuffling
   */
  private async generateShufflePermutation(seed: Uint8Array): Promise<number[]> {
    const deckSize = STANDARD_DECK.length;
    const indices = Array.from({ length: deckSize }, (_, i) => i);
    
    // Use Fisher-Yates shuffle with cryptographic randomness
    for (let i = deckSize - 1; i > 0; i--) {
      // Generate random index using hash of seed + position
      const randomSeed = await CryptoUtils.deriveKey(seed, i);
      const randomBytes = randomSeed.slice(0, 4);
      const randomInt = new DataView(randomBytes.buffer).getUint32(0, false);
      const j = randomInt % (i + 1);
      
      [indices[i], indices[j]] = [indices[j], indices[i]];
    }
    
    return indices;
  }

  /**
   * Generate zero-knowledge proof for shuffle operation
   * This proves the shuffle was performed correctly without revealing the permutation
   */
  private async generateZKShuffleProof(seed: Uint8Array, permutation: number[]): Promise<string> {
    // Simplified ZK proof - in production, use proper ZK-SNARKs or ZK-STARKs
    const proofData = {
      permutationCommitment: await CryptoUtils.createCommitment(permutation, seed),
      seedCommitment: await CryptoUtils.createCommitment(seed, seed),
      timestamp: Date.now()
    };
    
    return CryptoUtils.arrayBufferToBase64(
      new TextEncoder().encode(JSON.stringify(proofData))
    );
  }

  /**
   * Deal cards to specific player by partially decrypting them
   * Only the intended recipient can fully decrypt their cards
   */
  async dealCardsToPlayer(
    playerId: string, 
    cardIndices: number[], 
    requestingPlayerId: string
  ): Promise<DecryptionKey[]> {
    if (!this.playerIds.includes(playerId) || !this.playerIds.includes(requestingPlayerId)) {
      throw new Error('Invalid player IDs');
    }

    const decryptionKeys: DecryptionKey[] = [];

    for (const cardIndex of cardIndices) {
      const encryptedCard = this.encryptedDeck.find(card => card.cardIndex === cardIndex);
      if (!encryptedCard) {
        throw new Error(`Card ${cardIndex} not found in deck`);
      }

      // Generate partial decryption key for this player
      const partialKey = await this.generateCardEncryptionKey(requestingPlayerId, cardIndex);
      const keyPair = this.playerKeys.get(requestingPlayerId);
      if (!keyPair) throw new Error(`No key pair for player ${requestingPlayerId}`);

      const keyData = {
        cardIndex,
        playerId,
        partialKey: CryptoUtils.arrayBufferToBase64(partialKey),
        timestamp: Date.now()
      };

      const signature = await CryptoUtils.sign(keyData, keyPair.privateKey);

      const decryptionKey: DecryptionKey = {
        cardIndex,
        playerId,
        partialKey: keyData.partialKey,
        signature
      };

      decryptionKeys.push(decryptionKey);

      // Store decryption key for later use
      if (!this.decryptionKeys.has(playerId)) {
        this.decryptionKeys.set(playerId, new Map());
      }
      this.decryptionKeys.get(playerId)!.set(cardIndex, decryptionKey);
    }

    return decryptionKeys;
  }

  /**
   * Decrypt a card using all available decryption keys
   * All players except the card owner must provide their decryption keys
   */
  async decryptCard(cardIndex: number, playerId: string): Promise<string> {
    const encryptedCard = this.encryptedDeck.find(card => card.cardIndex === cardIndex);
    if (!encryptedCard) {
      throw new Error(`Card ${cardIndex} not found`);
    }

    let decryptedData = encryptedCard.encryptedData;

    // Apply decryption layers in reverse order of encryption
    const reversedLayers = [...encryptedCard.encryptionLayers].reverse();
    
    for (const layer of reversedLayers) {
      if (layer.playerId === playerId) {
        // Player can decrypt their own layer
        const decryptionKey = CryptoUtils.base64ToArrayBuffer(layer.encryptionKey);
        decryptedData = await CryptoUtils.decrypt(decryptedData, decryptionKey);
      } else {
        // Need decryption key from other player
        const playerKeys = this.decryptionKeys.get(layer.playerId);
        const cardKey = playerKeys?.get(cardIndex);
        
        if (!cardKey) {
          throw new Error(`Missing decryption key from player ${layer.playerId} for card ${cardIndex}`);
        }

        const decryptionKey = CryptoUtils.base64ToArrayBuffer(cardKey.partialKey);
        decryptedData = await CryptoUtils.decrypt(decryptedData, decryptionKey);
      }
    }

    return decryptedData;
  }

  /**
   * Verify shuffle proof from another player
   */
  async verifyShuffleProof(proof: ShuffleProof): Promise<boolean> {
    try {
      // Verify player signature
      const keyPair = this.playerKeys.get(proof.playerId);
      if (!keyPair) return false;

      const proofData = {
        playerId: proof.playerId,
        commitment: proof.commitment,
        zkProof: proof.zkProof
      };

      const signatureValid = await CryptoUtils.verify(
        proofData,
        proof.signature,
        keyPair.publicKey
      );

      // Verify commitment
      const commitmentValid = await CryptoUtils.verifyCommitment(
        proof.commitment,
        proof.nonce,
        proof.nonce
      );

      return signatureValid && commitmentValid;
    } catch {
      return false;
    }
  }

  /**
   * Get public encryption keys for all players
   */
  private async getEncryptionKeys(): Promise<Uint8Array[]> {
    const keys: Uint8Array[] = [];
    
    for (const [, keyPair] of this.playerKeys.entries()) {
      const publicKeyBuffer = await crypto.subtle.exportKey('spki', keyPair.publicKey);
      keys.push(new Uint8Array(publicKeyBuffer));
    }
    
    return keys;
  }

  /**
   * Get current deck state (for debugging/testing)
   */
  getDeckState(): CardEncryption[] {
    return [...this.encryptedDeck];
  }

  /**
   * Get all shuffle proofs
   */
  getShuffleProofs(): ShuffleProof[] {
    return [...this.shuffleProofs];
  }
}

/**
 * Utility functions for Mental Poker operations
 */
export class MentalPokerUtils {
  /**
   * Validate that a deck contains all 52 unique cards
   */
  static validateDeckCompleteness(cards: string[]): boolean {
    if (cards.length !== 52) return false;
    
    const cardSet = new Set(cards);
    return cardSet.size === 52 && 
           STANDARD_DECK.every(card => cardSet.has(card));
  }

  /**
   * Convert card string to human readable format
   */
  static formatCard(card: string): string {
    if (card.length !== 2) return card;
    
    const rank = card[0];
    const suit = card[1];
    
    const ranks: Record<string, string> = {
      'A': 'Ace', '2': '2', '3': '3', '4': '4', '5': '5', 
      '6': '6', '7': '7', '8': '8', '9': '9', 'T': '10',
      'J': 'Jack', 'Q': 'Queen', 'K': 'King'
    };
    
    const suits: Record<string, string> = {
      'C': '♣', 'D': '♦', 'H': '♥', 'S': '♠'
    };
    
    return `${ranks[rank] || rank}${suits[suit] || suit}`;
  }

  /**
   * Generate game statistics for Mental Poker session
   */
  static generateGameStats(context: MentalPokerContext) {
    return {
      gameId: context.gameId,
      playerCount: context.players.length,
      shuffleCount: context.shuffleProofs.length,
      encryptionLayers: context.players.length,
      timestamp: Date.now()
    };
  }
}